import * as React from "react";
import { Search, X } from "./icons";

import { Input } from "./input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";
import { cn } from "./utils";

export interface SearchInputProps
  extends Omit<React.ComponentProps<typeof Input>, "type"> {
  /**
   * Wrap the input in a `<form>` (default method GET) so URL-state pages
   * can use it directly: typing + Enter submits and Next picks up the
   * query param named by `name` (default "q"). Omit for client-state
   * callers that bind `value`/`onChange` themselves.
   */
  asForm?: boolean;
  /** Width preset for the field. Default `md` (w-64). */
  width?: "auto" | "sm" | "md" | "lg";
  /**
   * Fired after the clear (✕) affordance empties the field. The field is
   * already reset — `onChange` fires with an empty value (controlled), and
   * `asForm` navigates with the search param dropped to reset the page. Use
   * this only for extra reset work (clearing sibling filters, a page index, …).
   */
  onClear?: () => void;
  /** Hide the clear (✕) affordance even when the field has a value. Default `false`. */
  disableClear?: boolean;
}

const WIDTH = {
  auto: "",
  sm: "w-48",
  md: "w-64",
  lg: "w-80",
} as const;

export function SearchInput({
  asForm,
  width = "md",
  className,
  placeholder = "Search…",
  name = "q",
  value,
  defaultValue,
  onChange,
  onClear,
  disableClear,
  disabled,
  ref,
  ...inputProps
}: SearchInputProps) {
  const innerRef = React.useRef<HTMLInputElement>(null);
  const setRefs = React.useCallback(
    (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  // We show the ✕ whenever the field holds text. For controlled callers we read
  // `value`; for uncontrolled / `asForm` callers we track it off the input's own
  // change events (seeded from `defaultValue`).
  const isControlled = value !== undefined;
  const [uncontrolledFilled, setUncontrolledFilled] = React.useState(
    () => String(defaultValue ?? "").length > 0,
  );
  const hasValue = isControlled
    ? String(value ?? "").length > 0
    : uncontrolledFilled;
  const showClear = !disableClear && !disabled && hasValue;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isControlled) setUncontrolledFilled(e.target.value.length > 0);
    onChange?.(e);
  };

  const clear = React.useCallback(() => {
    const input = innerRef.current;
    if (input) {
      // Empty the field through the native value setter and dispatch a real
      // `input` event — this makes React fire `onChange` for controlled callers
      // *and* updates the DOM for uncontrolled / `asForm` callers, so one code
      // path resets both modes.
      const setNativeValue = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        "value",
      )?.set;
      setNativeValue?.call(input, "");
      input.dispatchEvent(new Event("input", { bubbles: true }));
      input.focus();
      // `asForm` pages read the query from the URL, so reset the page by
      // navigating with only the search param dropped — this preserves sibling
      // params (filters, section deep-links) that a bare form re-submit would
      // clobber, since the form serializes just its own `name` field.
      if (asForm && typeof window !== "undefined") {
        const url = new URL(window.location.href);
        if (url.searchParams.has(name)) {
          url.searchParams.delete(name);
          window.location.assign(url.toString());
        }
      }
    }
    if (!isControlled) setUncontrolledFilled(false);
    onClear?.();
  }, [asForm, isControlled, name, onClear]);

  const field = (
    <div className={cn("relative", WIDTH[width])}>
      <Search
        aria-hidden
        className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        ref={setRefs}
        type="search"
        name={name}
        placeholder={placeholder}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        disabled={disabled}
        className={cn(
          "appearance-none pl-8 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden",
          showClear && "pr-8",
          className,
        )}
        {...inputProps}
      />
      {showClear ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Clear search"
                onClick={clear}
                className="absolute right-1.5 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:text-foreground"
              >
                <X aria-hidden className="size-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Clear</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : null}
    </div>
  );
  return asForm ? <form className="contents">{field}</form> : field;
}
